import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, PhoneOff, Loader2, BarChart3 } from 'lucide-react';
import { useLivekitTest, LoadingState, CallState } from '@/hooks/useLivekitTest';
import { usePersonaAssessment } from '@/hooks/usePersonaAssessment';

interface SimpleVoiceProps {}

const SimpleVoice: React.FC<SimpleVoiceProps> = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('scenario') || '';

  const {
    loadingState,
    callState,
    callDuration,
    isSpeaking,
    sessionData,
    transcript,
    handleStartCall,
    handleEndCall,
    formatTime,
  } = useLivekitTest({ sessionId });

  const {
    generateAssessment,
    clearAssessment,
    assessment,
    loading: assessmentLoading,
    error: assessmentError,
  } = usePersonaAssessment();

  const isCallActive = callState === CallState.ACTIVE;
  const isLoading = loadingState === LoadingState.CONNECTING;
  const isLoadingSession = loadingState === LoadingState.LOADING;

  // Clear assessment when starting a new call
  useEffect(() => {
    if (isCallActive) {
      clearAssessment();
    }
  }, [isCallActive, clearAssessment]);

  // Auto-generate assessment when call ends
  useEffect(() => {
    const shouldGenerateAssessment =
      callState === CallState.ENDED &&
      transcript.length > 0 &&
      !assessment &&
      !assessmentLoading;

    if (shouldGenerateAssessment) {
      const transcriptText = transcript
        .map((msg) => `${msg.source === 'ai' ? 'Agent' : 'User'}: ${msg.message}`)
        .join('\n');

      generateAssessment(sessionId, transcriptText);
    }
  }, [callState]); // Only trigger on call state change

  const handleGenerateAssessment = async () => {
    if (assessmentLoading || assessment) {
      return; // Prevent duplicate requests
    }

    const transcriptText = transcript
      .map((msg) => `${msg.source === 'ai' ? 'Agent' : 'User'}: ${msg.message}`)
      .join('\n');

    await generateAssessment(sessionId, transcriptText);
  };

  const handleFinishCall = async () => {
    await handleEndCall();
    // Assessment will be triggered automatically by useEffect
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Show loading while fetching session
  if (isLoadingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white">Loading session...</h3>
          <p className="text-gray-400 mt-2">{sessionId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Test the persona</h1>
          <p className="text-gray-400">Session ID: {sessionId}</p>
          {sessionData && (
            <p className="text-gray-500 text-sm mt-1">
              {sessionData.scenario?.title || sessionData.scenario?.name || 'Test Session'}
            </p>
          )}
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Status */}
          <div className="text-center mb-8">
            {isCallActive ? (
              <>
                <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  isSpeaking ? 'bg-blue-500 animate-pulse' : 'bg-green-500'
                }`}>
                  <Phone className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {isSpeaking ? 'AI Speaking...' : 'Call Active'}
                </h2>
                <p className="text-4xl font-mono text-gray-700">{formatTime(callDuration)}</p>
              </>
            ) : (
              <>
                <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Phone className="w-12 h-12 text-gray-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {callState === CallState.ENDED ? 'Call Ended' : 'Ready to Connect'}
                </h2>
                <p className="text-gray-500">
                  {callState === CallState.ENDED
                    ? 'Call has ended successfully'
                    : 'Click the button below to start'}
                </p>
              </>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {/* Main Call Button */}
            <button
              onClick={isCallActive ? handleFinishCall : handleStartCall}
              disabled={isLoading}
              className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                isCallActive
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : isCallActive ? (
                <>
                  <PhoneOff className="w-6 h-6" />
                  <span>Finish</span>
                </>
              ) : (
                <>
                  <Phone className="w-6 h-6" />
                  <span>Start Call</span>
                </>
              )}
            </button>
          </div>

          {/* Transcript */}
          {transcript.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Transcript</h3>
                {!isCallActive && transcript.length > 0 && (
                  <button
                    onClick={handleGenerateAssessment}
                    disabled={assessmentLoading}
                    className="flex items-center gap-2 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assessmentLoading ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <BarChart3 className="w-3 h-3" />
                        <span>Get Assessment</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {transcript.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`text-sm p-2 rounded ${
                      msg.source === 'ai'
                        ? 'bg-blue-50 text-blue-900'
                        : 'bg-gray-50 text-gray-900'
                    }`}
                  >
                    <div className="font-semibold text-xs mb-1">
                      {msg.source === 'ai' ? '🤖 AI' : '👤 You'}
                    </div>
                    <div>{msg.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assessment Error */}
          {assessmentError && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
              {assessmentError}
            </div>
          )}

          {/* Assessment Results */}
          {assessment && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Persona Assessment</h3>

              {/* Overall Score */}
              <div className="text-center mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div className={`text-5xl font-bold ${getScoreColor(assessment.score)}`}>
                  {assessment.score}/100
                </div>
                <div className="text-lg font-semibold text-gray-700 mt-1">
                  {assessment.rating}
                </div>
                <p className="text-sm text-gray-600 mt-2">{assessment.summary}</p>
              </div>

              {/* Component Scores */}
              <div className="space-y-3 mb-4">
                <h4 className="text-xs font-semibold text-gray-700 uppercase">Detailed Breakdown</h4>
                {assessment.components.map((component, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-900">{component.name}</span>
                      <span className="text-sm font-bold text-blue-600">
                        {component.score}/{component.maxScore}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{component.feedback}</p>
                  </div>
                ))}
              </div>

              {/* Strengths */}
              {assessment.strengths.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-green-700 uppercase mb-2">✓ Strengths</h4>
                  <ul className="space-y-1">
                    {assessment.strengths.map((strength, idx) => (
                      <li key={idx} className="text-xs text-gray-700 flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Areas for Improvement */}
              {assessment.areasForImprovement.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-orange-700 uppercase mb-2">→ Areas for Improvement</h4>
                  <ul className="space-y-1">
                    {assessment.areasForImprovement.map((area, idx) => (
                      <li key={idx} className="text-xs text-gray-700 flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Examples */}
              {assessment.examples.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Conversation Examples</h4>
                  <div className="space-y-2">
                    {assessment.examples.map((example, idx) => (
                      <blockquote key={idx} className="text-xs italic text-gray-600 border-l-2 border-blue-300 pl-3 py-1">
                        "{example}"
                      </blockquote>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-400">
            Voice testing interface powered by LiveKit
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimpleVoice;
